import { Capacitor } from '@capacitor/core'
import AppConfig from './AppConfigPlugin'

export interface AppConfiguration {
  API_URL: string
  GOOGLE_MAPS_API_KEY: string
  WEATHER_API_KEY: string | null | undefined
  CUSTOMER: string
  TITLE: string
  LOGO: string
  DEFAULT_LOCALE: string
  PRIMARY_COLOR_HEX: string
  PRIMARY_COLOR_RGB: string
  SECONDARY_COLOR_HEX: string
  SECONDARY_COLOR_RGB: string
  DANGER_COLOR_HEX: string
  DANGER_COLOR_RGB: string
  HELP_DOCUMENTS_DOCTYPE: string | null | undefined
}

export const DEFAULT_CONFIG: AppConfiguration = {
  API_URL: process.env.REACT_APP_API_URL || '',
  GOOGLE_MAPS_API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  WEATHER_API_KEY: process.env.REACT_APP_WEATHER_API_KEY,
  CUSTOMER: process.env.REACT_APP_CUSTOMER || 'default',
  TITLE: process.env.REACT_APP_TITLE || 'Datos en campo',
  LOGO: process.env.REACT_APP_LOGO || 'logo-ines.png',
  DEFAULT_LOCALE: process.env.REACT_APP_DEFAULT_LOCALE || 'es-ES',
  PRIMARY_COLOR_HEX: process.env.REACT_APP_PRIMARY_COLOR_HEX || '#00a5d0',
  PRIMARY_COLOR_RGB: process.env.REACT_APP_PRIMARY_COLOR_RGB || '0, 165, 208',
  SECONDARY_COLOR_HEX: process.env.REACT_APP_SECONDARY_COLOR_HEX || '#2c4a7e',
  SECONDARY_COLOR_RGB:
    process.env.REACT_APP_SECONDARY_COLOR_RGB || '2, 102, 128',
  DANGER_COLOR_HEX: process.env.REACT_APP_DANGER_COLOR_HEX || '#eb445a',
  DANGER_COLOR_RGB: process.env.REACT_APP_DANGER_COLOR_RGB || '235, 68, 90',
  HELP_DOCUMENTS_DOCTYPE: process.env.REACT_APP_HELP_DOCUMENTS_DOCTYPE,
}

// Cached variables that are initialized once at startup
let cachedConfig: AppConfiguration | null = null
let configPromise: Promise<AppConfiguration> | null = null

async function initializeConfig(): Promise<AppConfiguration> {
  console.log('Initializing config...')
  console.log(
    'Is native platform?' +
      Capacitor.isNativePlatform() +
      ' Platform:' +
      Capacitor.getPlatform(),
  )

  if (Capacitor.isNativePlatform()) {
    try {
      if (!AppConfig.getConfig) {
        console.error('AppConfig.getConfig is not defined!')
        return DEFAULT_CONFIG
      }
      const config = await AppConfig.getConfig()
      //console.log('Config received from plugin: %o' + JSON.stringify(config))
      return {
        ...DEFAULT_CONFIG,
        ...config,
        GOOGLE_MAPS_API_KEY:
          config.GOOGLE_MAPS_API_KEY && config.GOOGLE_MAPS_API_KEY !== 'null'
            ? config.GOOGLE_MAPS_API_KEY
            : '',
        WEATHER_API_KEY:
          config.WEATHER_API_KEY && config.WEATHER_API_KEY !== 'null'
            ? config.WEATHER_API_KEY
            : null,
        HELP_DOCUMENTS_DOCTYPE:
          config.HELP_DOCUMENTS_DOCTYPE &&
          config.HELP_DOCUMENTS_DOCTYPE !== 'null'
            ? config.HELP_DOCUMENTS_DOCTYPE
            : null,
        // Normalize the RGB types that can come as array or string
        PRIMARY_COLOR_RGB:
          typeof config.PRIMARY_COLOR_RGB === 'string'
            ? config.PRIMARY_COLOR_RGB
            : Array.isArray(config.PRIMARY_COLOR_RGB)
              ? config.PRIMARY_COLOR_RGB.join(', ')
              : DEFAULT_CONFIG.PRIMARY_COLOR_RGB,
        SECONDARY_COLOR_RGB:
          typeof config.SECONDARY_COLOR_RGB === 'string'
            ? config.SECONDARY_COLOR_RGB
            : Array.isArray(config.SECONDARY_COLOR_RGB)
              ? config.SECONDARY_COLOR_RGB.join(', ')
              : DEFAULT_CONFIG.SECONDARY_COLOR_RGB,
        DANGER_COLOR_RGB:
          typeof config.DANGER_COLOR_RGB === 'string'
            ? config.DANGER_COLOR_RGB
            : Array.isArray(config.DANGER_COLOR_RGB)
              ? config.DANGER_COLOR_RGB.join(', ')
              : DEFAULT_CONFIG.DANGER_COLOR_RGB,
      }
    } catch (error) {
      console.error('Error initializing config:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      })
      return DEFAULT_CONFIG
    }
  } else {
    return DEFAULT_CONFIG
  }
}

/**
 * Gets the application configuration asynchronously.
 * The first call initializes the configuration, the subsequent calls return the same Promise.
 */
export async function getConfig(): Promise<AppConfiguration> {
  if (!configPromise) {
    configPromise = initializeConfig()
    // Cache the configuration when resolved
    configPromise
      .then((config) => {
        cachedConfig = config
        //console.log('Config cached: %o' + JSON.stringify(cachedConfig))
      })
      .catch((error) => {
        console.error('Error caching config:', error)
      })
  }
  return configPromise
}

/**
 * Initializes the configuration and caches it synchronously.
 * Must be called at the start of the application before using the configuration variables.
 */
export async function initializeConfigCache(): Promise<void> {
  const config = await getConfig()
  cachedConfig = config
}

/**
 * Gets a configuration variable synchronously.
 * Requires that initializeConfigCache() has been called previously.
 * If not initialized, returns the default value.
 */
export function getConfigValue<K extends keyof AppConfiguration>(
  key: K,
): AppConfiguration[K] {
  return cachedConfig?.[key] ?? DEFAULT_CONFIG[key]
}

// Export cached synchronous variables for direct use
export const CONFIG = {
  get API_URL() {
    const value = getConfigValue('API_URL')
    // Use fallback if no cached value and not a native platform
    // (in native platforms it should always come from the plugin)
    return value || 'http://localhost:3030'
  },
  get GOOGLE_MAPS_API_KEY() {
    return getConfigValue('GOOGLE_MAPS_API_KEY')
  },
  get WEATHER_API_KEY() {
    return getConfigValue('WEATHER_API_KEY')
  },
  get CUSTOMER() {
    return getConfigValue('CUSTOMER')
  },
  get TITLE() {
    return getConfigValue('TITLE')
  },
  get LOGO() {
    return getConfigValue('LOGO')
  },
  get DEFAULT_LOCALE() {
    return getConfigValue('DEFAULT_LOCALE')
  },
  get PRIMARY_COLOR_HEX() {
    return getConfigValue('PRIMARY_COLOR_HEX')
  },
  get PRIMARY_COLOR_RGB() {
    return getConfigValue('PRIMARY_COLOR_RGB')
  },
  get SECONDARY_COLOR_HEX() {
    return getConfigValue('SECONDARY_COLOR_HEX')
  },
  get SECONDARY_COLOR_RGB() {
    return getConfigValue('SECONDARY_COLOR_RGB')
  },
  get DANGER_COLOR_HEX() {
    return getConfigValue('DANGER_COLOR_HEX')
  },
  get DANGER_COLOR_RGB() {
    return getConfigValue('DANGER_COLOR_RGB')
  },
  get HELP_DOCUMENTS_DOCTYPE() {
    return getConfigValue('HELP_DOCUMENTS_DOCTYPE')
  },
}

export const MAX_IMAGE_WIDTH = 2048

export const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 100000,
}

export const GOOGLE_MAPS_DEFAULT_ZOOM = 15
